import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter'

function LabaRugi() {
  const { user } = useAuthStore()
  const [labaRugiData, setLabaRugiData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [startDay, setStartDay] = useState('1');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [coopProfile, setCoopProfile] = useState(null)
  const [branchInfo, setBranchInfo] = useState(null)
  const [logoBase64, setLogoBase64] = useState('')

  const { trustees } = useTrusteesFooter(startDate, endDate)

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg')
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => setLogoBase64(reader.result)
        reader.readAsDataURL(blob)
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo()
  }, [])

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('id-ID').format(value)
  }

  useEffect(() => {
    const today = new Date()
    const y = today.getFullYear()
    const m = String(today.getMonth() + 1).padStart(2, '0')
    const d = String(today.getDate()).padStart(2, '0')
    setStartYear(String(y))
    setStartMonth('01')
    setStartDay('01')
    setEndYear(String(y))
    setEndMonth(m)
    setEndDay(d)

    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info')
        setCoopProfile(response.data?.data?.coopProfile)
        setBranchInfo(response.data?.data?.branchInfo)
      } catch (error) {
        console.error('Error fetching profile info:', error)
      }
    }
    fetchProfileInfo()
  }, [])

  // Compose date strings from dropdown parts
  useEffect(() => {
    if (startYear && startMonth && startDay) {
      setStartDate(`${startYear}-${startMonth}-${startDay}`)
    }
  }, [startYear, startMonth, startDay])

  useEffect(() => {
    if (endYear && endMonth && endDay) {
      setEndDate(`${endYear}-${endMonth}-${endDay}`)
    }
  }, [endYear, endMonth, endDay])

  const fetchLabaRugiData = async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    try {
      const response = await api.get('/reports/laba-rugi', { params: { startDate, endDate } })
      setLabaRugiData(response.data?.data)
    } catch (error) {
      console.error('Error fetching laba rugi data:', error)
      setLabaRugiData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLabaRugiData()
  }, [startDate, endDate])

  const handlePrint = () => {
    if (!labaRugiData) return
    const printWindow = window.open('', '_blank')
    const htmlContent = `
      <html>
        <head>
          <title>Laba Rugi - ${formatDate(startDate)} s/d ${formatDate(endDate)}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; font-size: 11px; }
            .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .kop-logo { width: 70px; height: auto; }
            .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
            .kop-badan-hukum { font-size: 12px; margin: 3px 0; }
            .kop-alamat { font-size: 11px; margin: 2px 0; }
            .kop-kontak { font-size: 10px; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 6px; }
            th { background: #f0f0f0; }
            .text-right { text-align: right; }
            .bg-section { background: #e8f4f8; font-weight: bold; }
            .bg-total { background: #f9f9f9; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="kop-surat">
              <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
              <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
              <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
              <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
              <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
            </div>
          ${renderTableHTML('PENDAPATAN', labaRugiData.Pendapatan, labaRugiData.Totals?.Pendapatan)}
          ${renderTableHTML('BEBAN', labaRugiData.Beban, labaRugiData.Totals?.Beban)}
          <h4 class="bg-section" style="padding:5px">LABA BERSIH</h4>
          <table>
            <tbody>
              <tr class="bg-total"><td colspan="2">LABA BERSIH</td><td class="text-right">${formatCurrency(labaRugiData.Totals?.LabaBersih)}</td></tr>
            </tbody>
          </table>
        ${renderTrusteesHtml(trustees)}
        </body>
      </html>
    `
    printWindow.document.write(htmlContent)
    printWindow.document.close()
    printWindow.print()
  }

  const renderTableHTML = (title, items, total) => `
    <h4 class="bg-section" style="padding:5px">${title}</h4>
    <table>
      <thead><tr><th>Kode</th><th>Akun</th><th class="text-right">Saldo</th></tr></thead>
      <tbody>
        ${items?.map(i => `<tr><td>${i.KodeAkun}</td><td>${i.NamaAkun}</td><td class="text-right">${formatCurrency(Math.abs(i.Saldo))}</td></tr>`).join('')}
        <tr class="bg-total"><td colspan="2">TOTAL ${title}</td><td class="text-right">${formatCurrency(total)}</td></tr>
      </tbody>
    </table>
  `

  const handleExportExcel = () => {
    if (!labaRugiData) return
    const wb = XLSX.utils.book_new()
    let wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi'],
      ['LAPORAN LABA RUGI'],
      [`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}`],
      [],
      ['SEKSI', 'KODE AKUN', 'NAMA AKUN', 'SALDO']
    ]

    const addSectionToExcel = (title, items, total) => {
      wsData.push([title]);
      items.forEach(i => wsData.push(['', i.KodeAkun, i.NamaAkun, Math.abs(i.Saldo)]));
      wsData.push(['', '', `TOTAL ${title}`, total], []);
    }

    addSectionToExcel('PENDAPATAN', labaRugiData.Pendapatan, labaRugiData.Totals.Pendapatan)
    addSectionToExcel('BEBAN', labaRugiData.Beban, labaRugiData.Totals.Beban)
    wsData.push(['', '', 'LABA BERSIH', labaRugiData.Totals.LabaBersih])

    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 35 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(wb, ws, 'Laba Rugi')
    XLSX.writeFile(wb, `Laba_Rugi_${startDate}_sd_${endDate}.xlsx`)
  }

  const handleExportPDF = () => {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.width

    try {
      doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20)
    } catch (e) { console.log('Logo PDF failed:', e.message) }

    // Kop Surat Header
    doc.setFontSize(16).setFont(undefined, 'bold')
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
    doc.setFontSize(9).setFont(undefined, 'normal')
    doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
    doc.setFontSize(8).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
    doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })

    // Garis pemisah
    doc.setLineWidth(0.5)
    doc.line(10, 28, pageWidth - 10, 28)
    doc.setLineWidth(0.3)
    doc.line(10, 29, pageWidth - 10, 29)

    // Judul Laporan
    doc.setFontSize(11).setFont(undefined, 'bold')
    doc.text('LAPORAN LABA RUGI', pageWidth / 2, 35, { align: 'center' })
    doc.setFontSize(8).setFont(undefined, 'normal')
    doc.text(`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}`, pageWidth / 2, 40, { align: 'center' })

    let finalY = 45
    const sections = [
      { title: 'PENDAPATAN', data: labaRugiData.Pendapatan, total: labaRugiData.Totals.Pendapatan },
      { title: 'BEBAN', data: labaRugiData.Beban, total: labaRugiData.Totals.Beban }
    ]

    sections.forEach(sec => {
      doc.setFontSize(10).setFont(undefined, 'bold')
      doc.text(sec.title, 14, finalY)
      
      const body = sec.data.map(i => [i.KodeAkun, i.NamaAkun, formatCurrency(Math.abs(i.Saldo))])
      body.push([{ content: `TOTAL ${sec.title}`, colSpan: 2, styles: { fontStyle: 'bold' } }, { content: formatCurrency(sec.total), styles: { fontStyle: 'bold', halign: 'right' } }])

      autoTable(doc, {
        startY: finalY + 2,
        head: [['Kode', 'Nama Akun', 'Saldo']],
        body: body,
        theme: 'grid',
        styles: { fontSize: 8 },
        columnStyles: { 2: { halign: 'right' } },
        margin: { top: 30 }
      })
      finalY = doc.lastAutoTable.finalY + 10
    })

    // Add Laba Bersih
    doc.setFontSize(10).setFont(undefined, 'bold')
    doc.text('LABA BERSIH', 14, finalY)
    autoTable(doc, {
      startY: finalY + 2,
      body: [[{ content: 'LABA BERSIH', colSpan: 2, styles: { fontStyle: 'bold' } }, { content: formatCurrency(labaRugiData.Totals.LabaBersih), styles: { fontStyle: 'bold', halign: 'right' } }]],
      theme: 'grid',
      styles: { fontSize: 8 },
      columnStyles: { 2: { halign: 'right' } },
      margin: { top: 30 }
    })

    if (trustees.length > 0) {
      doc.setFontSize(6)
      const midX = pageWidth / 2
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus')
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas')
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit')
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit')

      let yL = doc.lastAutoTable.finalY + 8
      if (pengurus.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGURUS:', 14, yL)
        yL += 3
        doc.setFont(undefined, 'normal')
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL)
          yL += 3
        })
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL)
          yL += 3
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL)
            yL += 3
          })
        }
      }

      let yR = doc.lastAutoTable.finalY + 8
      if (pengawas.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGAWAS:', midX + 5, yR)
        yR += 3
        doc.setFont(undefined, 'normal')
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR)
          yR += 3
        })
      }
    }

    doc.save(`Laba_Rugi_${startDate}_sd_${endDate}.pdf`)
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h3 className="card-title m-0">Laporan Laba Rugi</h3>
          <div className="btn-group">
            <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={!labaRugiData || loading}>
              <i className="fas fa-file-excel mr-1"></i> Excel
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleExportPDF} disabled={!labaRugiData || loading}>
              <i className="fas fa-file-pdf mr-1"></i> PDF
            </button>
            <button className="btn btn-sm btn-light" onClick={handlePrint} disabled={!labaRugiData || loading}>
              <i className="fas fa-print mr-1"></i> Cetak
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded">
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL MULAI</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={startDay} onChange={(e) => setStartDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={startMonth} onChange={(e) => setStartMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL AKHIR</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={endDay} onChange={(e) => setEndDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={endMonth} onChange={(e) => setEndMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center p-5"><div className="spinner-border text-primary"></div><p>Memuat Data...</p></div>
          ) : !labaRugiData ? (
            <div className="alert alert-warning">Data tidak ditemukan untuk periode ini.</div>
          ) : (
            <div className="table-responsive">
              <LabaRugiSection title="PENDAPATAN" items={labaRugiData.Pendapatan} total={labaRugiData.Totals.Pendapatan} format={formatCurrency} />
              <LabaRugiSection title="BEBAN" items={labaRugiData.Beban} total={labaRugiData.Totals.Beban} format={formatCurrency} />
              <div className="mt-3">
                <h5 className="p-2" style={{ backgroundColor: '#e8f4f8', borderLeft: '4px solid #17a2b8' }}>LABA BERSIH</h5>
                <table className="table table-sm table-bordered table-hover">
                  <tbody>
                    <tr className="font-weight-bold table-info">
                      <td colSpan="2">LABA BERSIH</td>
                      <td className="text-right">{formatCurrency(labaRugiData.Totals.LabaBersih)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const LabaRugiSection = ({ title, items, total, format }) => (
  <>
    <h5 className="p-2 mt-3" style={{ backgroundColor: '#e8f4f8', borderLeft: '4px solid #17a2b8' }}>{title}</h5>
    <table className="table table-sm table-bordered table-hover">
      <thead className="thead-light">
        <tr><th>Kode Akun</th><th>Nama Akun</th><th className="text-right">Saldo</th></tr>
      </thead>
      <tbody>
        {items?.map((a, idx) => (
          <tr key={idx}><td>{a.KodeAkun}</td><td>{a.NamaAkun}</td><td className="text-right">{format(Math.abs(a.Saldo))}</td></tr>
        ))}
        <tr className="font-weight-bold table-secondary">
          <td colSpan="2">TOTAL {title}</td><td className="text-right">{format(total)}</td>
        </tr>
      </tbody>
    </table>
  </>
)

export default LabaRugi
